"use client";
import * as React from "react";
import { cn } from "@/lib/utils";
import { useImperativeHandle } from "react";
import { Button } from "@/components/ui/button";
import { Undo, X } from "lucide-react";

interface UseAutosizeTextAreaProps {
  textAreaRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  minHeight?: number;
  maxHeight?: number;
  triggerAutoSize: string;
}

export const useAutosizeTextArea = ({
  textAreaRef,
  triggerAutoSize,
  maxHeight = Number.MAX_SAFE_INTEGER,
  minHeight = 0,
}: UseAutosizeTextAreaProps) => {
  const [init, setInit] = React.useState(true);
  React.useEffect(() => {
    // We need to reset the height momentarily to get the correct scrollHeight for the textarea
    const offsetBorder = 6;
    const textAreaElement = textAreaRef.current;
    if (textAreaElement) {
      if (init) {
        textAreaElement.style.minHeight = `${minHeight + offsetBorder}px`;
        if (maxHeight > minHeight) {
          textAreaElement.style.maxHeight = `${maxHeight}px`;
        }
        setInit(false);
      }
      textAreaElement.style.height = `${minHeight + offsetBorder}px`;
      const scrollHeight = textAreaElement.scrollHeight;
      // We then set the height directly, outside of the render loop
      // Trying to set this with state or a ref will product an incorrect value.
      if (scrollHeight > maxHeight) {
        textAreaElement.style.height = `${maxHeight}px`;
      } else {
        textAreaElement.style.height = `${scrollHeight + offsetBorder}px`;
      }
    }
  }, [textAreaRef, triggerAutoSize, init, maxHeight, minHeight]);
};

export type AutosizeTextAreaRef = {
  textArea: HTMLTextAreaElement;
  maxHeight: number;
  minHeight: number;
};

type AutosizeTextAreaProps = {
  maxHeight?: number;
  minHeight?: number;
  onUndo?: () => void;
  showUndo?: boolean;
  onClear?: () => void;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const AutosizeTextarea = React.forwardRef<
  AutosizeTextAreaRef,
  AutosizeTextAreaProps
>(
  (
    {
      maxHeight = Number.MAX_SAFE_INTEGER,
      minHeight = 150,
      className,
      onChange,
      value,
      onUndo,
      showUndo = false,
      onClear,
      ...props
    }: AutosizeTextAreaProps,
    ref: React.Ref<AutosizeTextAreaRef>
  ) => {
    const textAreaRef = React.useRef<HTMLTextAreaElement | null>(null);
    const [triggerAutoSize, setTriggerAutoSize] = React.useState("");

    useAutosizeTextArea({
      textAreaRef,
      triggerAutoSize: triggerAutoSize,
      maxHeight,
      minHeight,
    });

    useImperativeHandle(ref, () => ({
      textArea: textAreaRef.current as HTMLTextAreaElement,
      focus: () => textAreaRef?.current?.focus(),
      maxHeight,
      minHeight,
    }));

    React.useEffect(() => {
      setTriggerAutoSize(value as string);
    }, [props?.defaultValue, value]);

    const hasValue = value !== undefined && value !== null && value !== "";
    const showButtons = (hasValue && onClear) || (showUndo && onUndo);

    return (
      <div className='relative'>
        <textarea
          {...props}
          value={value}
          ref={textAreaRef}
          className={cn(
            "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 outline-none",
            showButtons && "pr-20",
            className
          )}
          onChange={(e) => {
            setTriggerAutoSize(e.target.value);
            onChange?.(e);
          }}
        />
        {showButtons && (
          <div className='absolute right-2 top-2 flex items-center gap-1'>
            {hasValue && onClear && (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='h-6 w-6 hover:bg-transparent'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClear();
                }}
                tabIndex={-1}
              >
                <X className='h-4 w-4 text-muted-foreground' />
                <span className='sr-only'>Clear input</span>
              </Button>
            )}
            {showUndo && onUndo && (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='h-6 w-6 hover:bg-transparent'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onUndo();
                }}
                tabIndex={-1}
              >
                <Undo className='h-4 w-4 text-muted-foreground' />
                <span className='sr-only'>Undo changes</span>
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
);
AutosizeTextarea.displayName = "AutosizeTextarea";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  onUndo?: () => void;
  showUndo?: boolean;
  onClear?: () => void;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, onUndo, showUndo = false, onClear, value, ...props }, ref) => {
    const hasValue = value !== undefined && value !== null && value !== "";
    const showButtons = (hasValue && onClear) || (showUndo && onUndo);

    return (
      <div className='relative'>
        <textarea
          className={cn(
            "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            showButtons && "pr-20",
            className
          )}
          ref={ref}
          value={value}
          {...props}
        />
        {showButtons && (
          <div className='absolute right-2 top-2 flex items-center gap-1'>
            {hasValue && onClear && (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='h-6 w-6 hover:bg-transparent'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClear();
                }}
                tabIndex={-1}
              >
                <X className='h-4 w-4 text-muted-foreground' />
                <span className='sr-only'>Clear input</span>
              </Button>
            )}
            {showUndo && onUndo && (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='h-6 w-6 hover:bg-transparent'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onUndo();
                }}
                tabIndex={-1}
              >
                <Undo className='h-4 w-4 text-muted-foreground' />
                <span className='sr-only'>Undo changes</span>
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
);
Textarea.displayName = "Textarea";
